import React from "react";
import { useNavigate } from "react-router-dom";

interface IndexData {
  index_id: number;
  name: string;
  symbol: string;
  current_value: number;
  day_change: number;
  day_high: number;
  day_low: number;
  last_updated: string;
  component_count: number;
  advancing: number;
  declining: number;
}

interface IndexOverviewProps {
  indices?: IndexData[] | null; 
}

const IndexOverview: React.FC<IndexOverviewProps> = ({
  indices = [],
}) => {
  const navigate = useNavigate();

  const safeIndices: IndexData[] = Array.isArray(indices) ? indices : [];

  if (safeIndices.length === 0) {
    return (
      <div className="box has-text-centered">
        <p className="has-text-grey">No index data available</p>
      </div>
    );
  }

  const handleIndexClick = (symbol: string) => {
    navigate(`/index-dashboard/${symbol}`);
  };

  return (
    <div className="index-overview mb-5">
      <h2 className="title is-5 mb-4">
        <span className="icon">
          <i className="fa fa-chart-line"></i>
        </span>
        <span>Market Indices</span>
      </h2>

      <div className="columns is-multiline">
        {safeIndices.map((index) => {
          const advanceDeclineRatio =
            index.component_count > 0
              ? (index.advancing / index.component_count) * 100
              : 50;

          return (
            <div key={index.index_id} className="column is-4">
              <div
                className="box index-card"
                onClick={() => handleIndexClick(index.symbol)}
                style={{ cursor: 'pointer' }}
              >
                <div className="level is-mobile mb-2">
                  <div className="level-left">
                    <div className="level-item">
                      <div>
                        <p className="has-text-weight-bold is-size-6">
                          {index.name}
                        </p>
                        <p className="is-size-7 has-text-grey">
                          {index.symbol}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="level-right">
                    <div className="level-item">
                      <span
                        className={`tag ${
                          index.day_change >= 0
                            ? "is-success"
                            : "is-danger"
                        }`}
                      >
                        {index.day_change >= 0 ? "+" : ""}
                        {index.day_change.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="has-text-centered mb-3">
                  <p className="title is-4">
                    {index.current_value.toFixed(2)}
                  </p>

                  <div className="level is-mobile">
                    <div className="level-item has-text-centered">
                      <div>
                        <p className="heading">High</p>
                        <p className="title is-6 has-text-success">
                          {index.day_high.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="level-item has-text-centered">
                      <div>
                        <p className="heading">Low</p>
                        <p className="title is-6 has-text-danger">
                          {index.day_low.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="advance-decline mb-2">
                  <div className="level is-mobile mb-1">
                    <div className="level-left">
                      <span className="is-size-7">
                        <strong className="has-text-success">
                          {index.advancing}
                        </strong>{" "}
                        advancing
                      </span>
                    </div>

                    <div className="level-right">
                      <span className="is-size-7">
                        <strong className="has-text-danger">
                          {index.declining}
                        </strong>{" "}
                        declining
                      </span>
                    </div>
                  </div>

                  <progress
                    className={`progress is-small ${
                      advanceDeclineRatio >= 60
                        ? "is-success"
                        : advanceDeclineRatio >= 40
                        ? "is-warning"
                        : "is-danger"
                    }`}
                    value={advanceDeclineRatio}
                    max={100}
                  />
                </div>

                <p className="is-size-7 has-text-grey has-text-centered">
                  {new Date(index.last_updated).toLocaleTimeString()}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IndexOverview;
